import PdfIcon from '@/assets/icons/pdf_icon.svg';
import DocxIcon from '@/assets/icons/docx_icon.svg';
import XlsIcon from '@/assets/icons/xls_icon.svg';
import PptIcon from '@/assets/icons/ppt_icon.svg';

const FILE_CATEGORIES = {
  images: {
    icon: undefined,
    extensions: ['jpg', 'jpeg', 'gif', 'png'],
    mimeTypes: ['image/jpg', 'image/jpeg', 'image/gif', 'image/png'],
  },
  docs: {
    icon: DocxIcon,
    extensions: ['doc', 'docx'],
    mimeTypes: [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
  },
  ppt: {
    icon: PptIcon,
    extensions: ['ppt', 'pptx'],
    mimeTypes: [
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ],
  },
  xls: {
    icon: XlsIcon,
    extensions: ['xls', 'xlsx'],
    mimeTypes: [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ],
  },
  pdf: {
    icon: PdfIcon,
    extensions: ['pdf'],
    mimeTypes: ['application/pdf'],
  },
};

const ALLOWED_EXTENSIONS = new Set([
  '.jpg',
  '.jpeg',
  '.gif',
  '.png',
  '.doc',
  '.docx',
  '.ppt',
  '.pptx',
  '.xls',
  '.xlsx',
  '.pdf',
]);

const ALLOWED_MIME_TYPES = new Set([
  'image/jpg',
  'image/jpeg',
  'image/gif',
  'image/png',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/pdf',
]);

// Explicitly rejected
const DISALLOWED_EXTENSIONS = new Set(['.svg', '.csv']);
const DISALLOWED_MIME_TYPES = new Set(['image/svg+xml', 'text/csv']);
// Audio/Video rejected by prefix
const DISALLOWED_MIME_PREFIXES = ['audio/', 'video/'];

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024; // 15 * 1024 * 1024

export const formatFileSize = (bytes: number) => {
  if (!bytes) {
    return '';
  }
  const kb = bytes / 1024;
  if (kb < 1024) {
    return `${kb.toFixed(2)} KB`;
  }
  const mb = kb / 1024;
  return `${mb.toFixed(2)} MB`;
};

const getFileExtension = (nameOrUrl: string): string | undefined => {
  if (!nameOrUrl) {
    return undefined;
  }

  // Remove query params&fragments
  const cleanUrl = nameOrUrl.split(/[?#]/)[0];

  // Extract extension at the end of the string
  const match = cleanUrl.match(/\.([a-zA-Z0-9]+)$/);
  if (!match) {
    return undefined;
  }

  const ext = match[1].toLowerCase(); // "pdf", "jpg", "docx" etc
  return ext.toLowerCase();
};

const UPLOAD_REJECTED_REASONS = {
  FILE_TOO_LARGE: 'file_exceeds_size_limit',
  MIME_TYPE_NOT_SUPPORTED: 'mime_type_is_not_supported',
};

export const validateFile = (
  file: File,
): { allowed: boolean; reason?: string } => {
  const { name, type, size } = file;
  const ext = name ? getFileExtension(name) : null;
  const mime = type;

  if (size > MAX_FILE_SIZE_BYTES) {
    return {
      allowed: false,
      reason: UPLOAD_REJECTED_REASONS.FILE_TOO_LARGE,
    };
  }

  // Explicitly disallowed by MIME prefix (audio/video)
  if (mime && DISALLOWED_MIME_PREFIXES.some((p) => mime.startsWith(p))) {
    return {
      allowed: false,
      reason: UPLOAD_REJECTED_REASONS.MIME_TYPE_NOT_SUPPORTED,
    };
  }

  // Explicitly disallowed by extension/MIME
  if (
    (ext && DISALLOWED_EXTENSIONS.has(ext)) ||
    DISALLOWED_MIME_TYPES.has(mime)
  ) {
    return {
      allowed: false,
      reason: UPLOAD_REJECTED_REASONS.MIME_TYPE_NOT_SUPPORTED,
    };
  }

  // Allowed by extension
  if (ext && ALLOWED_EXTENSIONS.has(ext)) {
    return { allowed: true };
  }

  // Allowed by MIME (important for pasted screenshots)
  if (mime && ALLOWED_MIME_TYPES.has(mime)) {
    return { allowed: true };
  }

  // Everything else is not allowed
  return {
    allowed: false,
    reason: UPLOAD_REJECTED_REASONS.MIME_TYPE_NOT_SUPPORTED,
  };
};

export const getIconByUrl = (url: string) => {
  const ext = getFileExtension(url);
  if (!ext) {
    return undefined;
  }

  for (const categoryKey in FILE_CATEGORIES) {
    const category =
      FILE_CATEGORIES[categoryKey as keyof typeof FILE_CATEGORIES];
    if (category.extensions.includes(ext)) {
      return category.icon || undefined;
    }
  }

  return undefined;
};

export const downloadFile = (url: string, filename?: string) => {
  const link = document.createElement('a');
  link.href = url;

  if (filename) {
    link.download = filename;
  }

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadImage = async (url: string, filename: string) => {
  if (!url) {
    return;
  }

  // Fetch the file as a blob
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to download file: ${response.status}`);
  }

  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);

  // Trigger browser download
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(blobUrl);
};

export const mapFileNameToMediaType = (name?: string) => {
  if (!name) {
    return undefined;
  }

  const ext = getFileExtension(name);
  if (!ext) {
    return undefined;
  }

  const imageExts = ['jpg', 'jpeg', 'png', 'gif'];
  const docExts = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'];

  if (imageExts.includes(ext)) {
    return 'IMAGE';
  }

  if (docExts.includes(ext)) {
    return 'DOCUMENT';
  }

  return undefined;
};
